import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPut, apiPost, apiDownload } from '../services/api';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Link2,
  FileText,
  Receipt,
  Filter,
  Check,
  Download,
} from 'lucide-react';

interface Transaction {
  id: string;
  statement_id: string;
  transaction_date: string;
  description: string;
  amount: number;
  balance: number | null;
  transaction_type: string | null;
  reference: string | null;
  fee_amount: number | null;
  is_debit: boolean;
  is_fee: boolean;
  matched_invoice_id: string | null;
  matched_expense_id: string | null;
  matched_invoice_number: string | null;
  matched_expense_number: string | null;
  match_confidence: number | null;
  is_matched: boolean;
  is_selected: boolean;
  is_reconciled: boolean;
  user_notes: string | null;
  sort_order: number | null;
}

interface BankStatement {
  id: string;
  account_name: string | null;
  account_number: string | null;
  account_type: string | null;
  bank_name: string;
  currency: string;
  statement_period_start: string | null;
  statement_period_end: string | null;
  opening_balance: number | null;
  closing_balance: number | null;
  total_debits: number | null;
  total_credits: number | null;
  transaction_count: number;
  original_filename: string | null;
  status: string;
  transactions: Transaction[];
}

const typeColors: Record<string, string> = {
  fee: 'bg-orange-500/10 text-orange-400',
  receipt: 'bg-green-500/10 text-green-400',
  payment: 'bg-red-500/10 text-red-400',
  purchase: 'bg-purple-500/10 text-purple-400',
  transfer: 'bg-blue-500/10 text-blue-400',
  cash: 'bg-yellow-500/10 text-yellow-400',
  interest: 'bg-cyan-500/10 text-cyan-400',
  reversal: 'bg-pink-500/10 text-pink-400',
  credit: 'bg-green-500/10 text-green-400',
  debit: 'bg-red-500/10 text-red-400',
};

function formatCurrency(amount: number | null, currency = 'ZAR'): string {
  if (amount === null || amount === undefined) return '-';
  const symbols: Record<string, string> = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || 'R';
  const abs = Math.abs(amount);
  const formatted = `${symbol}${abs.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  return amount < 0 ? `-${formatted}` : formatted;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA');
}

type FilterType = 'all' | 'matched' | 'unmatched' | 'debits' | 'credits' | 'fees';

export default function BankStatementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [statement, setStatement] = useState<BankStatement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchStatement = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<BankStatement>(`/api/bank-statements/${id}`);
      setStatement(data);
      // Initialize selected from server state
      const selected = new Set<string>();
      data.transactions.forEach((tx) => {
        if (tx.is_selected) selected.add(tx.id);
      });
      setSelectedIds(selected);
    } catch (err) {
      console.error('Failed to fetch bank statement:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchStatement();
  }, [id]);

  const toggleSelect = async (txId: string) => {
    const newSelected = new Set(selectedIds);
    const isNowSelected = !newSelected.has(txId);
    if (isNowSelected) {
      newSelected.add(txId);
    } else {
      newSelected.delete(txId);
    }
    setSelectedIds(newSelected);

    try {
      await apiPut(`/api/bank-statements/${id}/transactions/${txId}`, {
        is_selected: isNowSelected,
      });
    } catch (err) {
      console.error('Failed to update transaction:', err);
    }
  };

  const handleReconcile = async () => {
    if (!statement) return;
    setIsReconciling(true);
    try {
      const txIds = Array.from(selectedIds);
      await apiPost(`/api/bank-statements/${id}/reconcile`, {
        transaction_ids: txIds.length > 0 ? txIds : null,
      });
      await fetchStatement();
    } catch (err: any) {
      alert(err.message || 'Failed to reconcile');
    } finally {
      setIsReconciling(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (id) params.set('statement_id', id);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const blob = await apiDownload(`/api/bank-statements/transactions/export?${params}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transactions_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const filteredTransactions = statement?.transactions.filter((tx) => {
    // Apply type filter
    switch (filter) {
      case 'matched': if (!tx.is_matched) return false; break;
      case 'unmatched': if (tx.is_matched || tx.is_fee) return false; break;
      case 'debits': if (tx.amount >= 0) return false; break;
      case 'credits': if (tx.amount <= 0) return false; break;
      case 'fees': if (!tx.is_fee) return false; break;
    }
    // Apply date range filter (ISO string comparison works for YYYY-MM-DD)
    if (dateFrom && tx.transaction_date < dateFrom) return false;
    if (dateTo && tx.transaction_date > dateTo) return false;
    return true;
  }) || [];

  const matchedCount = statement?.transactions.filter((t) => t.is_matched).length || 0;
  const reconciledCount = statement?.transactions.filter((t) => t.is_reconciled).length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading statement...</p>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-gray-500">Bank statement not found</p>
        <Link to="/admin/bank-statements" className="text-primary-500 hover:text-primary-400">
          Back to statements
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/bank-statements')}
          className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">
            {statement.original_filename || 'Bank Statement'}
          </h1>
          <p className="text-gray-400">
            {statement.bank_name} &middot; {statement.account_name || 'Account'}
            {statement.account_number && ` (***${statement.account_number.slice(-4)})`}
          </p>
        </div>
        <button
          onClick={handleReconcile}
          disabled={isReconciling || selectedIds.size === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <CheckCircle2 className="w-5 h-5" />
          {isReconciling ? 'Reconciling...' : `Reconcile (${selectedIds.size})`}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Period</p>
          <p className="text-sm text-white">
            {formatDate(statement.statement_period_start)} - {formatDate(statement.statement_period_end)}
          </p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Opening Balance</p>
          <p className="text-sm text-white">{formatCurrency(statement.opening_balance)}</p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Closing Balance</p>
          <p className="text-sm text-white">{formatCurrency(statement.closing_balance)}</p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Transactions</p>
          <p className="text-sm text-white">{statement.transaction_count}</p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Matched</p>
          <p className="text-sm text-green-400">{matchedCount} / {statement.transaction_count}</p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Reconciled</p>
          <p className="text-sm text-blue-400">{reconciledCount} / {statement.transaction_count}</p>
        </div>
      </div>

      {/* Debit/Credit Totals */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4 flex items-center justify-between">
          <span className="text-gray-400">Total Credits</span>
          <span className="text-green-400 font-medium">
            {formatCurrency(statement.total_credits)}
          </span>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4 flex items-center justify-between">
          <span className="text-gray-400">Total Debits</span>
          <span className="text-red-400 font-medium">
            {formatCurrency(statement.total_debits ? -statement.total_debits : null)}
          </span>
        </div>
      </div>

      {/* Date Range Filter + Export */}
      <div className="flex gap-4 flex-wrap items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-dark-800 border border-dark-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-dark-800 border border-dark-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="px-3 py-2 text-sm bg-dark-800 text-gray-400 hover:text-white rounded-lg transition-colors"
          >
            Clear
          </button>
        )}
        <button
          onClick={handleExportCSV}
          disabled={isExporting}
          className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-gray-300 hover:text-white rounded-lg transition-colors disabled:opacity-50 ml-auto"
        >
          <Download className="w-4 h-4" />
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap items-center">
        <Filter className="w-4 h-4 text-gray-500" />
        {(['all', 'matched', 'unmatched', 'credits', 'debits', 'fees'] as FilterType[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
              filter === f
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {f}
          </button>
        ))}
        <span className="text-sm text-gray-500 ml-2">
          {filteredTransactions.length} transactions
        </span>
      </div>

      {/* Transactions Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="p-3 w-10">
                  <span className="sr-only">Select</span>
                </th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Date</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Description</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Type</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Debit</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Credit</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Balance</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    No transactions match the filter
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className={`hover:bg-dark-800/50 ${
                      tx.is_reconciled ? 'opacity-60' : ''
                    } ${tx.is_matched ? 'bg-green-500/5' : ''}`}
                  >
                    <td className="p-3">
                      {tx.is_reconciled ? (
                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(tx.id)}
                          onChange={() => toggleSelect(tx.id)}
                          className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500 cursor-pointer"
                        />
                      )}
                    </td>
                    <td className="p-3 text-sm text-gray-300 whitespace-nowrap">
                      {formatDate(tx.transaction_date)}
                    </td>
                    <td className="p-3">
                      <div className="max-w-xs">
                        <p className="text-sm text-white truncate" title={tx.description}>
                          {tx.description}
                        </p>
                        {tx.reference && (
                          <p className="text-xs text-gray-500 truncate" title={tx.reference}>
                            Ref: {tx.reference}
                          </p>
                        )}
                        {tx.fee_amount && (
                          <p className="text-xs text-orange-400">
                            Fee: {formatCurrency(tx.fee_amount)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {tx.transaction_type && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                            typeColors[tx.transaction_type] || 'bg-gray-500/10 text-gray-400'
                          }`}
                        >
                          {tx.transaction_type}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right text-sm">
                      {tx.amount < 0 ? (
                        <span className="text-red-400">
                          {formatCurrency(Math.abs(tx.amount))}
                        </span>
                      ) : null}
                    </td>
                    <td className="p-3 text-right text-sm">
                      {tx.amount > 0 ? (
                        <span className="text-green-400">
                          {formatCurrency(tx.amount)}
                        </span>
                      ) : tx.amount === 0 ? (
                        <span className="text-gray-500">R0.00</span>
                      ) : null}
                    </td>
                    <td className="p-3 text-right text-sm text-gray-400">
                      {tx.balance !== null ? formatCurrency(tx.balance) : '-'}
                    </td>
                    <td className="p-3">
                      {tx.is_matched ? (
                        <div className="flex items-center gap-1">
                          <Link2 className="w-3 h-3 text-green-400" />
                          {tx.matched_invoice_id && (
                            <Link
                              to={`/admin/invoices/${tx.matched_invoice_id}`}
                              className="text-xs text-green-400 hover:text-green-300"
                            >
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {tx.matched_invoice_number || 'Invoice'}
                              </span>
                            </Link>
                          )}
                          {tx.matched_expense_id && (
                            <Link
                              to={`/admin/expenses/${tx.matched_expense_id}`}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              <span className="flex items-center gap-1">
                                <Receipt className="w-3 h-3" />
                                {tx.matched_expense_number || 'Expense'}
                              </span>
                            </Link>
                          )}
                          {tx.match_confidence !== null && (
                            <span className="text-xs text-gray-500 ml-1">
                              {Math.round(tx.match_confidence * 100)}%
                            </span>
                          )}
                        </div>
                      ) : tx.is_fee ? (
                        <span className="text-xs text-gray-500">Fee</span>
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-600" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
